import styled from "styled-components";
import { colors } from "common/const/colors";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import { useApiRequest } from "common/utils/api";
import { useEffect, useRef, useState } from "react";
import { Column } from "../../Layout/new/constLayout";
import { TextBody3, TextTitle4 } from "../..";
import { DataLoadingLayout } from "common/utils/DataLoadingLayout";
import { formatDateToCustomString } from "common/utils/dateUtil";
import { getDateFromString, getDateTimeFromUTCString } from "common/utils/get";
import { NotificationType } from "common/enum/NotificationType";
import { NotificationItem } from "./NotificationItem";
import { Divider } from "common/components/Etc/divider";
import { t } from "i18next";
import { I18N_KEYS } from "common/i18n/keys";

export const NotificationPanel = ({ onClose }) => {
  const [pageNum, setPageNum] = useState(1);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const {
    loading,
    data: notifications,
    refetch,
  } = useApiRequest({
    url: "/api/v1.0/notifications",
    method: REQUEST_METHOD.GET,
    queryParameter: { pageNum: 1, listNum: 10 },
  });

  // 페이지 변경 시 refetch
  useEffect(() => {
    refetch({ overrideParams: { pageNum, listNum: 10 } });
  }, [pageNum]);

  // 데이터 업데이트
  useEffect(() => {
    if (notifications?.items) {
      if (pageNum === 1) {
        setItems(notifications.items);
      } else {
        setItems((prev) => [...prev, ...notifications.items]);
      }

      console.log(notifications.items.length >= 10);

      setHasMore(notifications.items.length >= 10);
    }
  }, [notifications]);

  useEffect(() => {
    const handler = () => {
      setPageNum(1);
      refetch();
    };
    window.addEventListener("sse-message", handler);
    return () => window.removeEventListener("sse-message", handler);
  }, []);

  // 스크롤 감지
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollHeight - scrollTop - clientHeight < 50) {
      setPageNum((prev) => prev + 1);
    }
  };

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <NotificationPanelContainer ref={containerRef} onScroll={handleScroll}>
      {/* Title */}
      <NotificationPanelHeader>
        <TextTitle4>Notifications</TextTitle4>
      </NotificationPanelHeader>
      <DataLoadingLayout
        loading={loading && pageNum === 1}
        style={{ padding: "1rem" }}>
        {/* Items */}
        {items.length === 0 && (
          <NotificationItem notificationInfo={{ title: "No notifications." }} />
        )}
        {items.map((notificationInfo, index) => {
          const formattedNotificationInfo =
            formatNotificationInfo(notificationInfo);

          return (
            <>
              <NotificationItem notificationInfo={formattedNotificationInfo} />
              {index !== items.length - 1 && <Divider color={colors.Gray} />}
            </>
          );
        })}

        {/* 추가 로딩 인디케이터 */}
        {loading && pageNum > 1 && (
          <Column style={{ padding: "1rem 0" }}>
            <Divider color={colors.Gray} />
            <TextBody3 style={{ textAlign: "center" }}>Loading...</TextBody3>
          </Column>
        )}
      </DataLoadingLayout>
    </NotificationPanelContainer>
  );
};

const formatNotificationInfo = (notificationInfo) => {
  const reservation = notificationInfo.reservation;
  const passport = notificationInfo.passport;
  const notificationTag = notificationInfo.title.slice(
    0,
    notificationInfo.title.indexOf("]") + 1
  );
  const createdDateTime = getDateTimeFromUTCString(notificationInfo?.createdAt);
  const formattedCreatedAt = formatDateToCustomString(
    createdDateTime,
    "YYYY-MM-DD HH:mm:ss"
  );

  const baseNotification = {
    id: notificationInfo.id,
    isRead: notificationInfo.isRead,
    clubId: notificationInfo.payload?.clubId,
    createdAt: formattedCreatedAt,
    redirectUrl: `${notificationInfo.payload?.redirect}?targetDate=${reservation?.reservationDate ?? ""}${passport?.playerId ? `&playerId=${passport.playerId}` : ""}`,
  };

  const type = notificationInfo.payload?.type;
  const config = {
    BOOKING: {
      type: NotificationType.BOOKING,
      title: `${notificationTag} ${reservation?.name ?? "N/A"}`,
      content: getReservationInfoString(reservation),
      info: getPassportInfoString(passport),
    },
    CHECK_IN: {
      type: NotificationType.CHECK_IN,
      title: `${notificationTag} ${reservation?.name ?? "N/A"}`,
      content: getReservationInfoString(reservation),
      info: getPassportInfoString(passport),
    },
    CHANGE_PASSPORT: {
      type: NotificationType.CHANGE_PASSPORT,
      title: `${notificationTag} ${passport?.playerName ?? "N/A"}`,
      content: getReservationInfoString(reservation),
      info: getChangeFromMessage(notificationInfo.message),
    },
    CHECK_OUT: {
      type: NotificationType.CHECK_OUT,
      title: `${notificationTag} ${passport?.playerName ?? "N/A"}`,
      content: getReservationInfoString(reservation),
    },
    CANCEL: {
      type: NotificationType.CANCEL,
      title: `${notificationTag} ${reservation?.name ?? "N/A"}`,
      content: getReservationInfoString(reservation),
      info: reservation?.priceAmount,
    },
  };

  return config[type] ? { ...baseNotification, ...config[type] } : {};
};

const getReservationInfoString = (reservation) => {
  const date = formatDateToCustomString(
    getDateFromString(reservation?.reservationDate),
    `YYYY/MM/DD (d) ${reservation?.teeTime}`
  );

  return `${date ?? "N/A"}, ${reservation?.playerNum ?? "N/A"} players / ${reservation?.courseName ?? "N/A"}`;
};

const getPassportInfoString = (passport) => {
  const format = (label, value) =>
    value
      ? `${label} ${value}`
      : t(I18N_KEYS.notification.waitAssign).replace("${???}", label);

  return [
    format("Caddie", passport?.caddieName),
    format("Cart", passport?.cartName),
  ].join(" • ");
};

const getChangeFromMessage = (message) => {
  const caddieIdx = message.indexOf("Caddie");
  const cartIdx = message.indexOf("Cart");

  const results = [];

  if (caddieIdx !== -1) {
    const start = caddieIdx;
    const end = message.indexOf("\n", start);
    results.push(message.slice(start, end !== -1 ? end : undefined).trim());
  }

  if (cartIdx !== -1) {
    const start = cartIdx;
    const end = message.indexOf("\n", start);
    results.push(message.slice(start, end !== -1 ? end : undefined).trim());
  }

  return results.join(" / ");
};

const NotificationPanelContainer = styled.div`
  position: absolute;
  right: 0;
  min-width: 30rem;
  max-height: 35rem;
  background-color: var(--White);
  border-radius: 8px;
  border-color: var(--Gray);
  overflow: auto;
  scrollbar-width: none;

  /* shadow */
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
`;

const NotificationPanelHeader = styled.div`
  padding: 1rem;
  position: sticky;
  background-color: ${colors.White};
  top: 0;
  z-index: 1;
  border-bottom: 1px solid ${colors.Gray};
`;
